import PropTypes from "prop-types";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

// API's
import { getActiveFrequencies } from "@/PayPlans/api/frequencies/queries";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Utils
import { resolveDisableFrequencies } from "./utils";
import { getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";
import { storageGetItem } from "@/Common/helpers/Storage";

const getFrequencyLabel = (value = "MoM") => {
	const labels = {
		MoM: "Month over Month (MoM)",
		YoY: "Year over Year (YoY)",
	};
	return labels[value];
};

const frequencies = [
	{ label: "Month over Month (MoM)", value: "MoM" },
	{ label: "Year over Year (YoY)", value: "YoY" },
];

const FrequencySelector = (props) => {
	const {
		sx = {},
		disabled = false,
		readOnly = false,
		loading = false,
		section = "",
		make = "",
		selected = "MoM",
		sectionId = "",
		onChange = () => {},
		onDisabledFrequency = () => {},
	} = props;

	const storageLocation = storageGetItem("location");
	const locationOEMs = getSanitatedLocationOEMs(storageLocation?.oems || []);
	const locationId = storageLocation?.locationId;

	const { data, isLoading } = useQuery({
		queryKey: ["activeFrequencies", section, locationId],
		queryFn: async () =>
			getActiveFrequencies({
				locationId,
				section,
			}),
		staleTime: Infinity,
		enabled: !!(locationId && section),
	});

	const makeOem = locationOEMs?.find(
		(oemItem) =>
			oemItem.oemDisplayName === make ||
			oemItem.franchises?.find(
				(franchise) => (franchise.isCombined && franchise.groups.includes(make)) || franchise.franchise === make
			)
	);

	const disableFrequencies = resolveDisableFrequencies({
		...(data || {}),
		section,
		make,
		frequencies,
		makeOem,
		sectionId,
		locationOEMs,
	});

	useEffect(() => {
		onDisabledFrequency?.(disableFrequencies, data?.hasSalesPerformance);
	}, [disableFrequencies, data?.hasSalesPerformance, onDisabledFrequency]);

	return (
		<SmartSelector
			selectBy={"value"}
			options={frequencies}
			disabledOptions={disableFrequencies}
			selected={{ value: selected || "MoM", label: getFrequencyLabel(selected) }}
			disabled={disabled || readOnly || disableFrequencies.length === frequencies.length}
			loading={isLoading || loading}
			sx={sx}
			onChangeAction={onChange}
		/>
	);
};

FrequencySelector.propTypes = {
	sx: PropTypes.object,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	loading: PropTypes.bool,
	section: PropTypes.string,
	make: PropTypes.string,
	selected: PropTypes.string,
	sectionId: PropTypes.string,
	onChange: PropTypes.func,
	onDisabledFrequency: PropTypes.func,
};

export default FrequencySelector;
